import {
  assignableChoices,
  assignedChoice,
  assignmentPayload,
  choiceLabel,
  divergenceNote,
  effectiveRateNote,
  isOnUnprintable,
  rateOptions,
  unprintableAssignmentWarning,
  attachPayload,
  attachableSurcharges,
  noSurchargesNote,
  shouldPersist,
  surchargeChoicesOf,
  surchargeLabel,
  surchargeStateNote,
  unassignedNote,
  vatChoicesOf,
  withAttachment,
  unmatchedRateWarning,
  withheldNote,
} from './product-vat'
import { apiErrorMessage, customFetch } from '@agos/api-client'
import {
  getGetProductsQueryKey,
  useGetPizzaIngredients,
  useGetRecipes,
  useGetProductsProductIdAddonGroups,
  useGetProductsProductIdAddons,
  useGetSurcharges,
  useGetVatCategories,
  usePatchProductsProductId,
  usePostProducts,
  usePostRecipes,
  usePutRecipesRecipeId,
  usePutProductsProductIdAddons,
  usePutProductsProductIdSurchargesSurchargeId,
  usePutProductsProductIdVatCategory,
} from '@agos/api-client/menu'
import type { CategoryWithTranslations, Product, UploadedImage } from '@agos/api-client/model'
import { T, formatCents, formatCount, formatDecimal, formatMoney, ingredientGroups, ingredientPriceCents, normalisePizzaConfig, parseMoneyToCents, sizeCmFromName, tt, type BuilderIngredient, type PizzaConfig, useModalFocus } from '@agos/shared'

import { parseIngredientIds } from './pizza-ingredient-ids'
import {
  EMPTY_INGREDIENT,
  RecipeEditor,
  firstIncompleteRow,
  ingredientPayload,
  type IngredientDraft,
} from '../recipes/RecipeEditor'
import { RECIPE_CATEGORIES } from '../recipes/RecipeModal'
import { recipeRefusal, writesRecipe } from '../recipes/recipe-rights'
import { cn, toast, BUTTON_PRIMARY, ReadFailedBlock } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  ChefHat,
  Loader2,
  Pizza,
  Plus,
  Upload,
  Zap,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { AddonSelector, AllergenPicker } from './pickers'
import { groupedAddonIdsOf } from '../upsell/upsell-rules'
import { basePriceNote } from './base-price-note'
import { sizeCosting } from './size-costing'
import { pizzaSizeRows, type PizzaSizeRow } from './pizza-size-rows'

/**
 * The product form. Spec: `docs/pages/manager-menu/products.md`.
 *
 * The VAT fields are the rates as legacy stores them. §9.5 replaces this picker
 * with a VAT-category picker and adds surcharge attachment — after parity, as a
 * separate commit. Do not fold it in here.
 */

/**
 * §9.130 — the modes, as buttons. The pizza label names the consequence, which is what somebody
 * creating one needs to know: choosing it swaps the single price for a size table.
 *
 * **§9.235 — the number is the configuration's, not a constant.** The label said *Pizza (2
 * veľkosti)* while the franchise decides how many sizes it offers; the owner asked *"is that 2
 * dynamic? Since we can have custom sizes"*, and it was not. It counts the enabled sizes now, and
 * falls back to the plain word when the configuration has not loaded.
 *
 * **`kombo_pizza` is gone** — *"that is useless"*. It is a builder concept assembled in the order
 * screen, not a product type defined here.
 */
function productTypes(sizeCount: number) {
  return [
    { value: 'standard', label: tt('Štandardný'), icon: null },
    {
      value: 'pizza',
      label: sizeCount > 0 ? `${tt('Pizza')} (${formatCount(sizeCount)} ${tt('veľkosti')})` : tt('Pizza'),
      icon: Pizza,
    },
  ] as const
}


const UNIT_OPTIONS = ['', 'ks', 'porcia', 'g', 'kg', 'ml', 'l', 'cl']

const FIELD_CLASSES =
  'w-full px-4 py-2.5 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'
const LABEL_CLASSES = 'text-sm font-semibold text-foreground block mb-1.5'

function allergenIdsOf(product: Product | null): number[] {
  return (product?.allergens ?? '')
    .split(',')
    .map((part) => Number.parseInt(part.trim(), 10))
    .filter((id) => Number.isFinite(id))
}

export function ProductDialog({
  product,
  products,
  categories,
  mayEditRecipe,
  onClose,
  onSaved,
}: {
  product: Product | null
  products: Product[]
  categories: CategoryWithTranslations[]
  /**
   * Whether this person holds `recipes_edit` - §9.758, BACKEND.md §10A.413. **Required** (§9.480). Without it the
   * recipe block shows the product's recipe read-only, offers none to add, and the save never writes it - the product
   * is this person's to save and the recipe is not.
   */
  mayEditRecipe: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const isNewProduct = product === null

  const [name, setName] = useState(product?.name ?? '')
  const [description, setDescription] = useState(product?.description ?? '')
  const [kitchenDescription, setKitchenDescription] = useState(product?.kitchenDescription ?? '')
  const [showInKitchen, setShowInKitchen] = useState(Boolean(product?.showInKitchen))
  const [price, setPrice] = useState(String(product?.price ?? ''))

  /**
   * §9.5's VAT picker. Assigning a category snapshots **that** rate onto every line of every order for
   * this product; leaving it empty falls back to the register's per-channel setting. See `product-vat.ts`
   * for FB-184 — why an unprintable category is not offered at all.
   */
  // Seeded from the product now that the server projects it (§10A.3). It was always `null` on open,
  // which is why every note here said the current assignment could not be shown.
  const [vatCategoryId, setVatCategoryId] = useState<number | null>(product?.vatCategoryId ?? null)
  // Nothing projects `products.vat_category_id`, so an untouched picker knows nothing and must write
  // nothing — otherwise a save about a price strips a rate somebody chose last week.
  const [vatTouched, setVatTouched] = useState(false)
  const { data: vatData } = useGetVatCategories()
  const vatChoices = useMemo(() => vatChoicesOf(vatData), [vatData])
  const assigned = assignedChoice(vatCategoryId, vatChoices)
  const assignVat = usePutProductsProductIdVatCategory()
  const queryClient = useQueryClient()
  const { data: surchargeData } = useGetSurcharges()
  const attachable = useMemo(
    () => attachableSurcharges(surchargeChoicesOf(surchargeData)),
    [surchargeData],
  )
  // Which of them this product already carries — projected since §10A.3, so a row can say so instead
  // of the screen admitting it cannot tell. Held locally rather than read off the prop: these buttons
  // write immediately and leave the dialog open, so the prop is a snapshot that nothing refreshes
  // (FB-199, §9.102). The dialog is remounted per product, so seeding from the prop is enough.
  const [attachedIds, setAttachedIds] = useState<number[]>(() => product?.surchargeIds ?? [])
  const attachSurcharge = usePutProductsProductIdSurchargesSurchargeId({
    mutation: {
      onSuccess: (_result, variables) => {
        const attached = (variables.data as { attached?: boolean }).attached !== false
        // Only after the server agreed. An optimistic row here would claim an attachment that a 500
        // never made, on the one screen that says which products carry a deposit.
        setAttachedIds((current) => withAttachment(current, variables.surchargeId, attached))
        // …and so the list this dialog was opened from stops disagreeing with what was just written.
        void queryClient.invalidateQueries({ queryKey: getGetProductsQueryKey() })
        toast({
          title: attached ? tt('Príplatok priradený') : tt('Príplatok odobratý'),
        })
      },
      // §9.385 - a title-only toast. The surcharge endpoint names what it refused.
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Príplatok sa nepodarilo nastaviť'),
          description: apiErrorMessage(caught, tt('Server ho odmietol.')),
        }),
    },
  })
  const [categoryId, setCategoryId] = useState(String(product?.categoryId ?? ''))
  const [active, setActive] = useState(product?.active ?? true)
  const [visibleOnWeb, setVisibleOnWeb] = useState(product?.visibleOnWeb ?? true)
  // §9.155 — `!== false`, not `?? true`: an absent field reads as orderable, so a payload from
  // anything that has not learned the column keeps selling rather than silently stopping.
  const [orderable, setOrderable] = useState(product?.orderable !== false)
  const [sortOrder, setSortOrder] = useState(String(product?.sortOrder ?? 0))
  const [unit, setUnit] = useState(product?.unit ?? '')
  const [unitAmount, setUnitAmount] = useState(product?.unitAmount ?? '')
  const [imageUrl, setImageUrl] = useState(product?.imageUrl ?? '')
  const [vatDineIn, setVatDineIn] = useState(
    product?.vatRateDineIn != null ? String(product.vatRateDineIn) : '',
  )
  const [vatDelivery, setVatDelivery] = useState(
    product?.vatRateDelivery != null ? String(product.vatRateDelivery) : '',
  )
  const [allergens, setAllergens] = useState<number[]>(() => allergenIdsOf(product))
  const [productType, setProductType] = useState(product?.productType ?? 'standard')
  const [pizzaSubcategory, setPizzaSubcategory] = useState(product?.pizzaSubcategory ?? '')
  const [configPriced, setConfigPriced] = useState(Boolean(product?.configPriced))
  /**
   * §9.190 — which ingredients a config-priced pizza is made of.
   *
   * `products.pizza_ingredient_ids` is a real column, `Product` maps it and `ProductController`
   * accepts it. The dialog never sent it and never offered a control, so ticking *Dynamická cena*
   * gave a pizza priced at its base and nothing else, permanently.
   *
   * Read from the product as a JSON array of ids, which is the shape legacy writes.
   */
  const [pizzaIngredientIds, setPizzaIngredientIds] = useState<number[]>(() =>
    parseIngredientIds((product as { pizzaIngredientIds?: unknown } | undefined)?.pizzaIngredientIds),
  )
  const { data: ingredientData } = useGetPizzaIngredients()
  const pizzaIngredients = useMemo(
    () =>
      (Array.isArray(ingredientData) ? ingredientData : []).filter((row) => {
        const entry = row as { active?: boolean; isActive?: boolean }
        return entry.active !== false && entry.isActive !== false
      }) as unknown as BuilderIngredient[],
    [ingredientData],
  )
  const [sizeRows, setSizeRows] = useState<PizzaSizeRow[]>([])
  /* FB-845, §9.559 - **the catch below hands `pizzaSizeRows` a null config, which falls back to the two hard-coded
     defaults when the product has no stored sizes.** Measured on `restaurant_settings.pizza_config`: Šurany is 32 and
     50 (so the defaults happen to match), **Nesvady is 32 enabled, 40 enabled and 50 disabled** - so creating a pizza
     there while this read has failed offers a size they have switched **off** and omits the one they sell, and the save
     stores it as the franchise's own sizes. FB-796's family: §9.506 recovers a line's size by matching its name against
     the configured labels, and a stored size outside the config is what breaks that.

     **A product that HAS stored sizes is unaffected**, which is why the guard below is narrow: `pizzaSizeRows` appends
     every stored size the configured list does not contain, so the rows are the same whether or not this read
     answered. Two earlier drafts of this comment claimed otherwise and the premise tests disproved both. */
  const [pizzaConfigFailed, setPizzaConfigFailed] = useState(false)
  // §9.227 — the configuration was fetched, read once for the size rows and thrown away. The base
  // price per size lives on it, and that is what the owner asked to see.
  const [pizzaConfig, setPizzaConfig] = useState<PizzaConfig | null>(null)
  // §9.235 — how many sizes this franchise actually offers, for the product-type label.
  const enabledSizeCount = (pizzaConfig?.sizes ?? []).filter((size) => size.enabled !== false).length
  /**
   * The smallest offered size, which is what legacy prices the picker's chips at — §9.190.
   *
   * From the row's label (`32cm`) through `sizeCmFromName`, because that is where the number lives;
   * 32 when nothing is offered yet, which is the same default the customer's builder uses.
   */
  const pickerSizeCm =
    sizeRows
      .filter((row) => row.offered !== false)
      .map((row) => sizeCmFromName(row.label))
      .find((cm) => cm !== null) ?? 32
  const [addonIds, setAddonIds] = useState<number[]>([])
  const [isUploading, setIsUploading] = useState(false)
  /** The upload's own failure, rendered at the control rather than at the foot of the form - §9.435. */
  const [uploadError, setUploadError] = useState('')
  const [error, setError] = useState('')

  const { data: existingAddons } = useGetProductsProductIdAddons(product?.id ?? 0, {
    query: { enabled: product?.id !== undefined },
  })
  const [seededAddonsFrom, setSeededAddonsFrom] = useState<unknown>(undefined)
  if (existingAddons !== undefined && existingAddons !== seededAddonsFrom) {
    setSeededAddonsFrom(existingAddons)
    setAddonIds(existingAddons.map((addon) => addon.id).filter((id): id is number => id != null))
  }

  /* §9.719 - the add-ons this product has under a group are *Upsell produktov*'s: `PUT /addons` replaces the
     ungrouped links only (§10A.365), so drawn here they could be unticked and saved and stay where they were. They are
     left out of the list and named under it.
     §9.744 amended - read on `products` (`GET /products/{id}/addon-groups`, BACKEND.md §10A.397), not off the upsell
     screen's list, which asks `upsell`: a role that may edit products and not upsell was refused that read, and the
     grouped add-ons came back as ordinary ticks. Until the read answers the list is not drawn, and the save leaves the
     add-ons alone, so a list built without it is never written. */
  const groupedRead = useGetProductsProductIdAddonGroups(product?.id ?? 0, {
    query: { enabled: product?.id !== undefined },
  })
  const groupedAddonIds = useMemo(() => groupedAddonIdsOf(groupedRead.data), [groupedRead.data])
  /** Whether the dialog knows which add-ons are grouped - always for a product not saved yet, which has none. */
  const groupsKnown = product?.id === undefined || groupedRead.data !== undefined
  const ungroupedAddonIds = addonIds.filter((id) => !groupedAddonIds.includes(id))

  const createProduct = usePostProducts()
  const updateProduct = usePatchProductsProductId()
  const saveAddons = usePutProductsProductIdAddons()

  // The size rows depend on the franchise's pizza configuration, which is a
  // separate untyped blob (§14.6).
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const response = await customFetch<Partial<PizzaConfig> | null>(
          '/api/v1/settings/pizza-config',
        )
        if (cancelled) return
        const config = normalisePizzaConfig(response)
        setPizzaConfig(config)
        setPizzaConfigFailed(false)
        setSizeRows(pizzaSizeRows(config, product?.pizzaSizes ?? null, isNewProduct))
      } catch {
        if (!cancelled) {
          setPizzaConfig(null)
          /* The rows are still drawn - an existing pizza's stored sizes are worth reading, and a blank size table
             would say the pizza has none (§9.531's *decline the write, not the view*). What changes is that the save
             is refused for a pizza while this is true. */
          setPizzaConfigFailed(true)
          setSizeRows(pizzaSizeRows(null, product?.pizzaSizes ?? null, isNewProduct))
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [product?.pizzaSizes, isNewProduct])

  const patchSizeRow = (index: number, patch: Partial<PizzaSizeRow>) =>
    setSizeRows((rows) => rows.map((row, position) => (position === index ? { ...row, ...patch } : row)))

  /**
   * Upload one file, and say so **beside the button** when it fails - §9.435, FB owner report.
   *
   * **The owner: *"There is no error, nothing."*** There was an error. It went into `setError`, which
   * renders in the dialog's shared line at the foot of a very long form - roughly two hundred lines
   * of markup below the file picker, and off-screen while the picker is in view. So a reproducible
   * server fault was indistinguishable from a dead button.
   *
   * The shared line keeps the save's own errors. This one is the upload's, where the person clicking
   * can read it.
   */
  const uploadImage = async (file: File) => {
    setIsUploading(true)
    setUploadError('')
    try {
      const form = new FormData()
      form.append('image', file)
      const response = await customFetch<Partial<UploadedImage>>('/api/v1/upload-image', {
        method: 'POST',
        body: form,
      })
      if (response?.url) setImageUrl(response.url)
      else setUploadError(tt('Server nevrátil adresu obrázka.'))
    } catch (caught) {
      setUploadError(apiErrorMessage(caught, tt('Obrázok sa nepodarilo nahrať.')))
    } finally {
      setIsUploading(false)
    }
  }

  /**
   * The recipe, written after the product — §9.221.
   *
   * **Second, and with the id the server just gave back**, which is exactly what legacy's submit
   * does: a recipe is stored against `product_id`, so a new product has nothing to hang one on
   * until it exists.
   *
   * **An untouched recipe is not written.** Opening the block to read it must not create an empty
   * one, so nothing goes unless something changed — legacy tracks the same thing.
   */
  const persistRecipe = (productId: number, done: () => void) => {
    // §9.758 - untouched, or not this person's to write: the product is saved and the recipe is left alone.
    if (!writesRecipe(recipeTouched, mayEditRecipe)) {
      done()
      return
    }
    const bad = firstIncompleteRow(recipeRows)
    if (bad !== -1) {
      setError(`${tt('Ingrediencia č.')} ${bad + 1} ${tt('nemá názov alebo množstvo.')}`)
      return
    }

    const request = {
      name: name.trim(),
      category: recipeCategory,
      description: recipeDescription.trim() || undefined,
      servings: Number.parseInt(recipeServings, 10) || 1,
      prepTime: Number.parseInt(recipePrepTime, 10) || undefined,
      productId,
      ingredients: ingredientPayload(recipeRows),
    }
    const handlers = {
      onSuccess: () => done(),
      onError: (caught: unknown) =>
        setError(apiErrorMessage(caught, tt('Produkt sa uložil, recept nie.'))),
    }
    if (existingRecipe?.id !== undefined) {
      updateRecipe.mutate({ recipeId: existingRecipe.id, data: request }, handlers)
    } else {
      createRecipe.mutate({ data: request }, handlers)
    }
  }

  const save = () => {
    if (name.trim().length < 2) {
      setError(tt('Meno je povinné'))
      return
    }

    const isPizza = productType === 'pizza'
    const offeredSizes = sizeRows
      .filter((row) => row.offered !== false)
      .map(({ offered: _offered, ...rest }) => rest)

    // §9.378, FB-673 - **an offered size needs a price, and this is where the broken row came
    // from.** `ServerPricing` skips a size whose price is blank and then refuses the *whole order*
    // when a line names it - the owner's *"Táto veľkosť už nie je v ponuke"* from a till that was
    // drawing the button at 0,00 €. The parser no longer offers such a size; this stops it being
    // written in the first place, and says which one rather than quietly dropping it.
    const unpriced = offeredSizes.filter((row) => String(row.price ?? '').trim() === '')
    if (isPizza && !configPriced && unpriced.length > 0) {
      setError(
        `${tt('Veľkosť bez ceny sa nedá predať')}: ${unpriced.map((row) => row.label).join(', ')} - ${tt('zadajte cenu alebo veľkosť vypnite.')}`,
      )
      return
    }

    // A pizza priced per size takes its headline price from the first offered
    // size; a config-priced one keeps whatever the form holds.
    const headlinePrice =
      isPizza && !configPriced ? Number(offeredSizes[0]?.price ?? 0) || 0 : Number(price) || 0

    const payload = {
      name: name.trim(),
      description: description || null,
      kitchenDescription: kitchenDescription || null,
      showInKitchen,
      price: headlinePrice,
      categoryId: categoryId ? Number(categoryId) : null,
      active,
      visibleOnWeb,
      orderable,
      sortOrder: Number(sortOrder) || 0,
      unit: unit || null,
      unitAmount: unitAmount || null,
      imageUrl: imageUrl || null,
      allergens: allergens.length > 0 ? allergens.join(',') : null,
      vatRateDineIn: vatDineIn === '' ? null : Number(vatDineIn),
      vatRateDelivery: vatDelivery === '' ? null : Number(vatDelivery),
      productType,
      pizzaSizes: isPizza ? JSON.stringify(offeredSizes) : null,
      // §9.20: kept for every product, not only a pizza. The field name is historical — the column
      // is `pizza_subcategory` and renaming the wire field would be a §8 break for a cosmetic gain.
      pizzaSubcategory: pizzaSubcategory || null,
      configPriced: isPizza ? configPriced : false,
      // §9.190 — legacy's shape: a JSON array of ids, and null when the pizza is not priced this way.
      pizzaIngredientIds:
        isPizza && configPriced ? JSON.stringify(pizzaIngredientIds) : null,
    }

    setError('')

    const persistAddons = (productId: number) => {
      // §9.744 amended - nothing is written from a list built without knowing which add-ons are grouped.
      if (!groupsKnown) return
      saveAddons.mutate({ productId, data: { addonProductIds: ungroupedAddonIds } })
    }

    /**
     * The VAT category is its own endpoint, not a field on the product.
     *
     * Written **only when the picker was touched**. `null` genuinely means *back to the register's
     * per-channel fallback*, so it has to be sendable — but the editor cannot read the current
     * assignment (nothing projects it), and sending an untouched blank would clear a rate it never saw.
     */
    const persistVat = (productId: number) => {
      if (!shouldPersist(vatTouched)) return
      assignVat.mutate({ productId, data: assignmentPayload(vatCategoryId) })
    }

    const existingProductId = product?.id
    if (existingProductId !== undefined) {
      updateProduct.mutate(
        { productId: existingProductId, data: payload },
        {
          onSuccess: () => {
            persistAddons(existingProductId)
            persistVat(existingProductId)
            toast({ title: tt('Uložené'), description: tt('Produkt bol upravený.') })
            // §9.181 — when the save was pressed to reach the recipe, the form stays and the recipe
            // opens over it. Calling `onSaved()` here would close the dialog the modal sits on.
            persistRecipe(existingProductId, onSaved)
          },
          onError: (caught) =>
            setError(apiErrorMessage(caught, tt('Nepodarilo sa uložiť produkt.'))),
        },
      )
      return
    }

    createProduct.mutate(
      { data: payload },
      {
        onSuccess: (created) => {
          const newId = (created as { id?: number } | undefined)?.id
          if (newId !== undefined && addonIds.length > 0) persistAddons(newId)
          if (newId !== undefined && vatCategoryId !== null) persistVat(newId)
          toast({ title: tt('Úspech'), description: tt('Produkt bol pridaný.') })
          // §9.181 — the product now has an id, which is the thing a recipe needs.
          if (newId === undefined) onSaved()
          else persistRecipe(newId, onSaved)
        },
        onError: (caught) => setError(apiErrorMessage(caught, tt('Nepodarilo sa pridať produkt.'))),
      },
    )
  }

  /**
   * §9.181 — the product whose recipe is open, if any.
   *
   * A recipe is stored against `product_id`, so a product that has not been saved yet has nothing to
   * attach one to. That is why this was missing rather than merely misplaced: for a new product the
   * button saves first and opens the recipe on the id the server just gave back.
   */
  /**
   * §9.221 — the recipe, edited in this form.
   *
   * Collapsed until asked for, exactly as legacy is: opening it must not create anything, and
   * `recipeTouched` is what keeps a read from becoming a write.
   */
  const [recipeOpen, setRecipeOpen] = useState(false)
  const [recipeTouched, setRecipeTouched] = useState(false)
  const [recipeRows, setRecipeRows] = useState<IngredientDraft[]>([])
  const [recipeCategory, setRecipeCategory] = useState(RECIPE_CATEGORIES[0] ?? 'Pizza')
  const [recipeDescription, setRecipeDescription] = useState('')
  const [recipePrepTime, setRecipePrepTime] = useState('')
  const [recipeServings, setRecipeServings] = useState('1')
  const { data: recipeList, isLoading: recipeLoading } = useGetRecipes()
  // A plain `find`, not a `useMemo`: the hydration below sets state during render, which the React
  // compiler will not memoize around — `ci.sh --strict` refuses it, and the list is ninety rows.
  const existingRecipe =
    product?.id === undefined
      ? undefined
      : ((recipeList ?? []) as { id?: number; productId?: number }[]).find(
          (row) => row.productId === product.id,
        )

  const createRecipe = usePostRecipes()
  const updateRecipe = usePutRecipesRecipeId()

  /**
   * The product's existing recipe, poured into the form once — §9.221.
   *
   * `GET /recipes` already carries `ingredients` on every row, so opening a product costs no extra
   * request. Poured **once**, keyed on the recipe id: re-running it on every render would undo
   * whatever somebody had just typed, which is the trap §9.126 records on the order builder.
   */
  const [loadedRecipe, setLoadedRecipe] = useState<number | null>(null)
  if (existingRecipe?.id !== undefined && existingRecipe.id !== loadedRecipe) {
    setLoadedRecipe(existingRecipe.id)
    const row = existingRecipe as {
      category?: string
      description?: string
      prepTime?: number
      servings?: number
      ingredients?: {
        stockItemId?: number
        productId?: number
        name?: string
        quantity?: number
        unit?: string
        cost?: string | number
      }[]
    }
    setRecipeCategory(row.category ?? RECIPE_CATEGORIES[0] ?? 'Pizza')
    setRecipeDescription(row.description ?? '')
    setRecipePrepTime(row.prepTime === undefined ? '' : String(row.prepTime))
    setRecipeServings(String(row.servings ?? 1))
    setRecipeRows(
      (row.ingredients ?? []).map((ingredient) => ({
        stockItemId: ingredient.stockItemId ?? null,
        productId: ingredient.productId ?? null,
        name: ingredient.name ?? '',
        quantity: String(ingredient.quantity ?? ''),
        unit: ingredient.unit ?? 'g',
        cost: String(parseMoneyToCents(ingredient.cost) / 100),
      })),
    )
  }


  const isSaving =
    createProduct.isPending ||
    updateProduct.isPending ||
    createRecipe.isPending ||
    updateRecipe.isPending

  /* §9.487, `TODO.md` 3c - the trap and the Escape together. The close is unconditional: this is a form
     whose submit is a separate button, so closing mid-typing loses a draft nobody committed, which is what
     its own cancel does anyway. */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, onClose)

  return (
    <div
      data-product-dialog
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-dialog-title"
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      {/* §9.487 - the role is on the wrapper because the wrapper **is** the scrim, and the trap's container
          is this card: `ConfirmDialog`'s pattern since §9.441, and §9.469's words - *"the modals carry
          role=dialog on the scrim and want the card inside it"*. */}
      <div
        ref={panelRef}
        onKeyDown={onKeyDown}
        className="bg-card rounded-3xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl border border-border"
      >
        <div className="px-6 py-5 border-b border-border flex justify-between items-center bg-muted/30">
          <h2 id="product-dialog-title" className="text-xl font-bold font-display">
            {product ? tt('Upraviť produkt') : tt('Nový produkt')}
          </h2>
          <button
            type="button"
            data-dialog-cancel
            aria-label={tt('Zatvoriť')}
            onClick={onClose}
            className="p-2 hover:bg-background rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-5">
          {/* §9.130 - a *mode*, not one of a list: it decides whether the form asks for one price or
            * a size table, so it is asked first and pressed rather than picked out of a dropdown.
            * The owner's *"predtým som si prepol"* is this control, in the place legacy keeps it. */}
          <div>
            <label className={LABEL_CLASSES}>
              <T>Typ produktu</T>
            </label>
            <div className="flex gap-2" data-product-type-switch>
              {productTypes(enabledSizeCount).map((type) => {
                const Glyph = type.icon
                return (
                  <button
                    key={type.value}
                    type="button"
                    data-product-type={type.value}
                    aria-pressed={productType === type.value}
                    onClick={() => setProductType(type.value)}
                    className={cn(
                      'flex-1 py-2.5 rounded-xl font-bold text-sm border transition-all flex items-center justify-center gap-1.5',
                      productType === type.value
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border text-muted-foreground hover:border-primary/40',
                    )}
                  >
                    {Glyph !== null && <Glyph className="w-4 h-4" />}
                    {type.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* §9.215 - *why podkategória is not next to kategória*. It sat two hundred lines below,
            * under the price row, because §9.20 moved it out of the pizza block and put it where
            * there was room. They are one question: where does this appear on the menu. */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-product-identity>
            <div>
              <label className={LABEL_CLASSES} htmlFor="product-nazov-produktu">
                <T>Názov produktu</T>
              </label>
              <input id="product-nazov-produktu" value={name} onChange={(event) => setName(event.target.value)} className={FIELD_CLASSES} />
            </div>
            <div>
              <label className={LABEL_CLASSES} htmlFor="product-kategoria">
                <T>Kategória</T>
              </label>
              <select
                id="product-kategoria"
                value={categoryId}
                onChange={(event) => setCategoryId(event.target.value)}
                className={FIELD_CLASSES}
              >
                <option value="">{tt('Nezaradené')}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={LABEL_CLASSES} htmlFor="product-podkategoria">
                <T>Podkategória</T>
              </label>
              <input
                id="product-podkategoria"
                value={pizzaSubcategory}
                onChange={(event) => setPizzaSubcategory(event.target.value)}
                className={FIELD_CLASSES}
                data-product-subcategory
              />
              <p className="text-xs text-muted-foreground mt-1">
                <T>Voliteľné. Produkty s rovnakou podkategóriou sa v ponuke zobrazia spolu.</T>
              </p>
            </div>
          </div>

          <div>
            <label className={LABEL_CLASSES} htmlFor="product-popis">
              <T>Popis</T>
            </label>
            <textarea
              id="product-popis"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={2}
              className={cn(FIELD_CLASSES, 'resize-none')}
            />
          </div>

          <div>
            <label className={LABEL_CLASSES} htmlFor="product-popis-pre-kuchynu">
              <T>Popis pre kuchyňu</T>
            </label>
            <textarea
              id="product-popis-pre-kuchynu"
              value={kitchenDescription}
              onChange={(event) => setKitchenDescription(event.target.value)}
              rows={2}
              className={cn(FIELD_CLASSES, 'resize-none')}
            />
            <label className="flex items-center gap-2 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showInKitchen}
                onChange={(event) => setShowInKitchen(event.target.checked)}
                className="w-4 h-4 rounded accent-primary"
              />
              <span className="text-sm">
                <T>Zobraziť v kuchyni</T>
              </span>
            </label>
          </div>

          {/* §9.5, renamed by §9.326 - the owner's *"I still have in new produkt Sadzba DPH main,
            * Sadzba DPH - na mieste, Sadzba DPH - rozvoz"*.
            *
            * **Three controls were called *Sadzba DPH* and this one is not a rate.** It writes
            * `vatCategoryId`, and the javadoc further down already said what that does: *"the picker
            * never supplies the document a rate at all - it is asked only for `slotFor(letter)`"*.
            * A5 made it permanent - the owner answered *"the channel rate wins"* - so a category is
            * a reporting label and the receipt's VAT letter, and never a rate.
            *
            * So it is named for what it decides. Removing it would lose the letter, which the
            * receipt needs; leaving it called *Sadzba DPH* is the confusion being reported. */}
          <div className="rounded-xl border border-border p-3 space-y-2" data-vat-picker>
            <label className={LABEL_CLASSES} htmlFor="product-sadzba-dph">
              <T>Písmeno DPH na doklade (kategória)</T>
            </label>
            <select
              id="product-sadzba-dph"
              value={vatCategoryId === null ? '' : String(vatCategoryId)}
              onChange={(event) => {
                setVatTouched(true)
                setVatCategoryId(event.target.value === '' ? null : Number(event.target.value))
              }}
              aria-label={tt('Písmeno DPH na doklade (kategória)')}
              data-vat-select
              className={FIELD_CLASSES}
            >
              <option value="">
                {vatTouched ? tt('Podľa nastavenia pokladne (kanál)') : tt('Nemeniť')}
              </option>
              {assignableChoices(vatChoices).map((choice) => (
                <option key={choice.id} value={choice.id}>
                  {choiceLabel(choice)}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-muted-foreground" data-vat-effect>
              {vatTouched || assigned !== null ? effectiveRateNote(assigned) : unassignedNote()}
            </p>
            {/* FB-184: a rate the till cannot print would make this product unsellable, so it is withheld
              * rather than offered — and the omission is explained, because silence reads as *we only have
              * two rates*. */}
            {withheldNote(vatChoices) !== '' && (
              <p className="text-[11px] text-amber-500" data-vat-withheld>
                {withheldNote(vatChoices)}
              </p>
            )}

            {/* §9.280 - the warning belongs on the click that creates the disagreement, and only when
              * this product's two channel rates actually differ. */}
            {divergenceNote(assigned, vatDineIn, vatDelivery) !== '' && (
              <p className="text-[11px] text-amber-500" data-vat-divergence>
                {divergenceNote(assigned, vatDineIn, vatDelivery)}
              </p>
            )}

            {/* §9.29 answered the owner's "2X DPH" the wrong way round, and it was about tax -
              * §9.280, FB-438. It called these two *evidenčné sadzby* and said the picker above
              * charges. Traced end to end, the **fiškál receipt** charges from exactly these two,
              * choosing by channel (`FiskalReceiptService.rateFor`, §10A.67, as legacy does), and the
              * picker never supplies the document a rate at all — it is asked only for
              * `slotFor(letter)`, which device index a letter prints to.
              *
              * Measured: all 173 products carry both, 129 of them 5 % on the spot against 19 %
              * delivered, and none has a category — so nothing has been mischarged. */}
            <div className="border-t border-border pt-2 mt-1 space-y-2">
              <p className="text-[11px] text-muted-foreground" data-vat-legacy-note>
                <T>
                  Toto sú jediné dve sadzby, ktoré účtujú - na mieste jednou, pri rozvoze druhou.
                  Pole vyššie nie je sadzba: určuje písmeno DPH na doklade a index v pokladni.
                </T>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL_CLASSES} htmlFor="product-sadzba-dph-na-mieste">
                    <T>Sadzba DPH - na mieste</T>
                  </label>
                  <select
                    id="product-sadzba-dph-na-mieste"
                    value={vatDineIn}
                    onChange={(event) => setVatDineIn(event.target.value)}
                    aria-label={tt('Sadzba DPH - na mieste')}
                    data-vat-dine-in
                    className={FIELD_CLASSES}
                  >
                    {rateOptions(vatChoices, vatDineIn).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_CLASSES} htmlFor="product-sadzba-dph-rozvoz">
                    <T>Sadzba DPH - rozvoz</T>
                  </label>
                  <select
                    id="product-sadzba-dph-rozvoz"
                    value={vatDelivery}
                    onChange={(event) => setVatDelivery(event.target.value)}
                    aria-label={tt('Sadzba DPH - rozvoz')}
                    data-vat-delivery
                    className={FIELD_CLASSES}
                  >
                    {rateOptions(vatChoices, vatDelivery).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {/* FB-848 - a stored rate the register has no category for is banded onto a neighbouring
                  letter and charged at *that* rate, with the document looking perfectly correct. Said
                  here, under the two boxes that hold it, because the fix is to pick one of the rates
                  named in the sentence. */}
              {unmatchedRateWarning(vatDineIn, vatChoices) !== '' && (
                <p className="text-[11px] text-destructive" data-vat-dine-in-unmatched>
                  {unmatchedRateWarning(vatDineIn, vatChoices)}
                </p>
              )}
              {unmatchedRateWarning(vatDelivery, vatChoices) !== '' && (
                <p className="text-[11px] text-destructive" data-vat-delivery-unmatched>
                  {unmatchedRateWarning(vatDelivery, vatChoices)}
                </p>
              )}
            </div>
          </div>

          {/* §9.5's second half - deposits, charged as their own receipt line. `surchargeIds` is
            * projected now, so each row says whether it is attached; the two explicit buttons stay
            * because they write immediately and a checkbox would imply the dialog's *Uložiť*. */}
          <div className="rounded-xl border border-border p-3 space-y-2" data-surcharge-picker>
            <label className={LABEL_CLASSES}>
              <T>Príplatky a zálohy</T>
            </label>
            {attachable.length === 0 ? (
              <p className="text-[11px] text-muted-foreground" data-no-surcharges>
                {noSurchargesNote()}
              </p>
            ) : (
              <>
                {attachable.map((choice) => (
                  <div
                    key={choice.id}
                    className="flex items-center gap-2"
                    data-surcharge-row={choice.id}
                    data-surcharge-attached={attachedIds.includes(choice.id) ? 'yes' : 'no'}
                  >
                    <span className="flex-1 text-xs truncate">
                      {surchargeLabel(choice)}
                      {attachedIds.includes(choice.id) && (
                        <span className="ml-1.5 text-[10px] font-bold text-emerald-500">
                          <T>priradené</T>
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      data-attach-surcharge={choice.id}
                      disabled={product?.id === undefined || attachSurcharge.isPending}
                      onClick={() => {
                        if (product?.id === undefined) return
                        attachSurcharge.mutate({
                          productId: product.id,
                          surchargeId: choice.id,
                          data: attachPayload(true),
                        })
                      }}
                      className="min-h-[36px] px-3 rounded-lg border border-border text-xs font-semibold disabled:opacity-50"
                    >
                      <T>Priradiť</T>
                    </button>
                    <button
                      type="button"
                      data-detach-surcharge={choice.id}
                      disabled={product?.id === undefined || attachSurcharge.isPending}
                      onClick={() => {
                        if (product?.id === undefined) return
                        attachSurcharge.mutate({
                          productId: product.id,
                          surchargeId: choice.id,
                          data: attachPayload(false),
                        })
                      }}
                      className="min-h-[36px] px-3 rounded-lg border border-border text-xs font-semibold disabled:opacity-50"
                    >
                      <T>Odobrať</T>
                    </button>
                  </div>
                ))}
                <p className="text-[11px] text-muted-foreground" data-surcharge-state>
                  {surchargeStateNote()}
                </p>
                {product?.id === undefined && (
                  <p className="text-[11px] text-muted-foreground" data-surcharge-needs-product>
                    <T>Najprv produkt uložte - príplatok sa priraďuje k existujúcemu produktu.</T>
                  </p>
                )}
              </>
            )}
            {/* An assignment made through the API or in SQL that this picker would not have offered. */}
            {isOnUnprintable(vatCategoryId, vatChoices) && (
              <p className="text-[11px] text-red-500" data-vat-unprintable>
                {unprintableAssignmentWarning(assigned)}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-product-measures>
            {/* §9.235 - a single price contradicts the block below it once *Dynamická cena* is on:
              * the price is the base plus the toppings, per size. Hidden rather than disabled,
              * because a greyed box still reads as a number somebody should fill in. */}
            <div className={configPriced ? 'hidden' : undefined} data-price-field>
              <label className={LABEL_CLASSES} htmlFor="product-cena">
                <T>Cena (€)</T>
              </label>
              <input
                id="product-cena"
                type="number"
                step="0.01"
                value={price}
                onChange={(event) => setPrice(event.target.value)}
                aria-label={tt('Cena (€)')}
                className={FIELD_CLASSES}
              />
            </div>
            <div>
              <label className={LABEL_CLASSES} htmlFor="product-mnozstvo">
                <T>Množstvo</T>
              </label>
              <input
                id="product-mnozstvo"
                value={unitAmount}
                onChange={(event) => setUnitAmount(event.target.value)}
                className={FIELD_CLASSES}
              />
            </div>
            <div>
              <label className={LABEL_CLASSES} htmlFor="product-jednotka">
                <T>Jednotka</T>
              </label>
              <select
                id="product-jednotka"
                value={unit}
                onChange={(event) => setUnit(event.target.value)}
                className={FIELD_CLASSES}
              >
                {UNIT_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option || tt('- bez jednotky -')}
                  </option>
                ))}
              </select>
            </div>
            {/* §9.215 - last, not between the price and the size. *Poradie* decides where the
              * product is drawn in a list; the three before it describe the thing being sold. */}
            <div>
              <label className={LABEL_CLASSES} htmlFor="product-poradie">
                <T>Poradie</T>
              </label>
              <input
                id="product-poradie"
                type="number"
                value={sortOrder}
                onChange={(event) => setSortOrder(event.target.value)}
                className={FIELD_CLASSES}
              />
            </div>
          </div>

          {productType === 'pizza' && (
            <div className="space-y-3 border border-border rounded-xl p-4 bg-muted/20">
              {/* §9.222 - legacy's switch. It was a bare checkbox, which is §9.199's complaint one
                * screen over: the rest of the system draws a switch, and this control decides
                * whether the number in the price field is used at all. The line underneath changes
                * with the state, which is the point of the card rather than decoration. */}
              <button
                type="button"
                role="switch"
                aria-checked={configPriced}
                aria-label={tt('Dynamická cena')}
                data-config-priced={configPriced ? 'on' : 'off'}
                onClick={() => setConfigPriced(!configPriced)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all text-left',
                  configPriced
                    ? 'border-violet-500 bg-violet-500/10'
                    : 'border-border bg-background hover:border-violet-400/50',
                )}
              >
                <Zap
                  className={cn(
                    'w-4 h-4 shrink-0',
                    configPriced ? 'text-violet-500' : 'text-muted-foreground',
                  )}
                />
                <span className="flex-1 min-w-0">
                  <span
                    className={cn('block font-bold', configPriced ? 'text-violet-600' : 'text-foreground')}
                  >
                    <T>Dynamická cena</T>
                  </span>
                  <span className="block text-xs font-normal text-muted-foreground mt-0.5">
                    {configPriced ? (
                      <T>Cena sa počíta automaticky z konfigurácie pizze a sumy ingrediencií.</T>
                    ) : (
                      <T>Zapnúť automatický výpočet ceny z konfigurácie pizze a ingrediencií.</T>
                    )}
                  </span>
                </span>
                <span
                  className={cn(
                    'w-10 h-6 rounded-full transition-all relative shrink-0',
                    configPriced ? 'bg-violet-500' : 'bg-muted',
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-1 w-4 h-4 rounded-full bg-white transition-all shadow',
                      configPriced ? 'right-1' : 'left-1',
                    )}
                  />
                </span>
              </button>

              {/* §9.190 - the control the switch always needed. A config-priced pizza is priced from
                  its ingredients, and there was no way to say which it has. Chips grouped by
                  category, as legacy does: a pizza is eight of forty, and forty checkboxes is the
                  screen the owner objected to. */}
              {configPriced && (
                <div className="space-y-3" data-pizza-ingredient-picker>
                  {/* §9.222 - violet, which is the colour legacy reserves for dynamic pricing, and
                    * the one the recipe rows below already use for a product-as-ingredient. */}
                  <p className="text-xs font-bold text-violet-600 uppercase tracking-wider flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5" /> <T>Výber ingrediencií</T>
                  </p>
                  {ingredientGroups(pizzaIngredients, true).map((group) => (
                    <div key={group.category} data-ingredient-group={group.category}>
                      <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-1.5">
                        {group.category}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {group.ingredients.map((ingredient) => {
                          const chosen = pizzaIngredientIds.includes(ingredient.id)
                          return (
                            <button
                              key={ingredient.id}
                              type="button"
                              data-pizza-ingredient={ingredient.id}
                              data-chosen={chosen}
                              onClick={() =>
                                setPizzaIngredientIds((current) =>
                                  chosen
                                    ? current.filter((id) => id !== ingredient.id)
                                    : [...current, ingredient.id],
                                )
                              }
                              className={cn(
                                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-semibold transition-all disabled:opacity-50',
                                chosen
                                  ? 'border-violet-500 bg-violet-500/15 text-violet-700'
                                  : 'border-border bg-background text-muted-foreground hover:border-violet-400/50 hover:text-foreground',
                              )}
                            >
                              {chosen && <span aria-hidden="true">✓</span>}
                              {ingredient.name}
                              <span className="text-muted-foreground">
                                +{formatCents(ingredientPriceCents(ingredient, pickerSizeCm))}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                  {/* The number the switch took away: with no per-size price to read, *what will
                      this cost* is otherwise unanswerable until the pizza is on the customer site. */}
                  <p className="text-xs font-semibold" data-pizza-ingredient-total>
                    <T>Ingrediencie spolu</T>:{' '}
                    {formatCents(
                      pizzaIngredients
                        .filter((ingredient) => pizzaIngredientIds.includes(ingredient.id))
                        .reduce(
                          (total, ingredient) =>
                            total + ingredientPriceCents(ingredient, pickerSizeCm),
                          0,
                        ),
                    )}
                  </p>

                  {/* §9.235 - legacy's *Hmotnosti (voliteľné)*: the sizes belong with the dynamic
                    * price, not opposite it. FB-385 recorded that AGOS had them in the fixed branch
                    * and left it; the owner asked twice, which settles it. */}
                <div className="space-y-2" data-weights>
                  {/* §9.236 - legacy's own title, and legacy's own columns: the tick, the size, the
                    * weight, and a **computed** price. There is no price to type under dynamic
                    * pricing; a box beside the computed figure would be a number somebody can change
                    * next to one that contradicts it. */}
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    <T>Hmotnosti (voliteľné)</T>
                  </p>
                  {/* §9.291, FB-456 - iterate the **rows**, not the costing. `sizeCosting` filters
                    * `offered !== false`, so unticking a size removed its own checkbox from the list
                    * and there was no way to tick it back. The costing is still what prices the row;
                    * it is looked up per row instead of driving the loop. */}
                  {sizeRows.map((row, index) => {
                      const size = sizeCosting(
                        pizzaConfig,
                        sizeRows,
                        pizzaIngredients,
                        pizzaIngredientIds,
                      ).find((entry) => entry.label === row.label)
                      return (
                        <div key={row.label} className="flex items-center gap-2" data-weight-row={row.label}>
                          <input
                            type="checkbox"
                            checked={row.offered !== false}
                            onChange={(event) => patchSizeRow(index, { offered: event.target.checked })}
                            aria-label={`${tt('Ponúkať')} ${row.label}`}
                            className="w-4 h-4 rounded accent-primary"
                          />
                          <span className="w-16 text-sm font-bold text-amber-600">{row.label}</span>
                          <input
                            type="number"
                            min="0"
                            value={row.weightG}
                            onChange={(event) => patchSizeRow(index, { weightG: event.target.value })}
                            placeholder={tt('napr. 450')}
                            aria-label={`${tt('Hmotnosť')} ${row.label}`}
                            className={cn(FIELD_CLASSES, 'flex-1 py-1.5')}
                          />
                          <span
                            data-computed-price={size?.computedPriceCents ?? ''}
                            className="w-24 shrink-0 text-sm font-bold text-violet-600 text-right tabular-nums"
                            title={tt('Vypočítaná cena')}
                          >
                            {size?.computedPriceCents == null
                              ? '-'
                              : formatCents(size.computedPriceCents)}
                          </span>
                        </div>
                      )
                    },
                  )}
                </div>

                  {/* §9.227 - what the base contributes, per offered size. The owner: *"in dynamická
                    * cena for pizza is missing základ… thats what is making the price"*. A reading,
                    * not a control: the base price is edited in the pizza configuration. */}
                  {pizzaConfigFailed && (
                    <p className="text-xs text-destructive leading-relaxed" data-pizza-config-error>
                      <T>
                        Nastavenia veľkostí pizze sa nepodarilo načítať, takže nevieme, ktoré veľkosti táto
                        prevádzka ponúka. Veľkosti nižšie sú len predvolené - novú pizzu sa zatiaľ uložiť nedá.
                      </T>
                    </p>
                  )}

                  {basePriceNote(pizzaConfig, sizeRows) !== '' && (
                    <p className="text-xs text-muted-foreground" data-base-price-note>
                      <T>Základ</T>: {basePriceNote(pizzaConfig, sizeRows)}
                    </p>
                  )}

                  {/* §9.227 - *"vypočítaná cena based on everything selected"*. The same sum the
                    * server runs on save, against the draft on screen, so the number can be seen
                    * before it is stored. A preview: the server recomputes and remains the record. */}
                  {sizeCosting(pizzaConfig, sizeRows, pizzaIngredients, pizzaIngredientIds).map(
                    (size) => (
                      <div
                        key={size.label}
                        data-size-costing={size.label}
                        className="flex items-center justify-between gap-2 text-xs text-muted-foreground"
                      >
                        <span className="font-semibold">{size.label}</span>
                        {size.costCents === null ? (
                          // Not a zero — some ingredient or the base has no cost. Saying which is
                          // how somebody knows where to go and fill one in.
                          <span data-size-costing-unknown>
                            <T>Náklad neznámy</T> ({size.withCost}/{size.chosen})
                          </span>
                        ) : (
                          <span className="tabular-nums" data-size-costing-cost={size.costCents}>
                            <T>Náklad</T> {formatMoney(size.costCents / 100)}
                            {size.marginPercent !== null && (
                              <>
                                {' · '}
                                <T>marža</T>{' '}
                                <span
                                  className={
                                    size.marginPercent >= 0 ? 'text-emerald-600' : 'text-red-600'
                                  }
                                >
                                  {formatDecimal(size.marginPercent, 0)} %
                                </span>
                              </>
                            )}
                          </span>
                        )}
                      </div>
                    ),
                  )}
                </div>
              )}

            </div>
          )}

          <div>
            <label className={LABEL_CLASSES}>
              <T>Alergény</T>
            </label>
            <AllergenPicker value={allergens} onChange={setAllergens} />
          </div>

          <div>
            <label className={LABEL_CLASSES}>
              <T>Doplnky k produktu</T>
            </label>
            {groupsKnown ? (
              <AddonSelector
                productId={product?.id ?? null}
                products={products.filter((one) => one.id === undefined || !groupedAddonIds.includes(one.id))}
                selectedIds={ungroupedAddonIds}
                onChange={setAddonIds}
              />
            ) : groupedRead.isError ? (
              <ReadFailedBlock
                marker="product-addons"
                title={tt('Doplnky k produktu sa nepodarilo načítať')}
                onRetry={() => void groupedRead.refetch()}
              >
                {tt(
                  'Nevieme, ktoré doplnky sú v skupinách, takže zoznam sa teraz nedá upraviť a uloženie doplnky nezmení.',
                )}
              </ReadFailedBlock>
            ) : (
              <p className="text-xs text-muted-foreground" data-product-addons-loading>
                <T>Načítavam doplnky…</T>
              </p>
            )}
            {/* §9.719 - the grouped ones by name, so a product's whole offer can be read here even though only the
                ungrouped part is edited here. */}
            {groupedAddonIds.length > 0 && (
              <p className="mt-1 text-xs text-muted-foreground" data-product-grouped-addons>
                <T>V skupinách (spravuje sa v Upsell produktov):</T>{' '}
                {groupedAddonIds
                  .map((id) => products.find((one) => one.id === id)?.name ?? `#${String(id)}`)
                  .join(', ')}
              </p>
            )}
            {/* §9.711 - this list edits the ungrouped add-ons only (§10A.365), so it says where the rest live. §9.719 -
                in a new tab, so following it does not throw away a half-edited product. */}
            <p className="mt-1 text-xs text-muted-foreground" data-product-upsell-note>
              <T>Doplnky v skupinách (s limitom alebo spoločnou cenou) sa spravujú na stránke</T>{' '}
              <a
                href="/upsell"
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-primary underline"
                data-upsell-link
              >
                <T>Upsell produktov</T>
              </a>
              .
            </p>
          </div>

          <div>
            <label className={LABEL_CLASSES}>
              <T>Obrázok</T>
            </label>
            <div className="flex items-center gap-3">
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt={name}
                  className="w-16 h-16 rounded-xl object-cover border border-border"
                />
              )}
              <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border text-sm font-semibold cursor-pointer hover:bg-muted/50">
                {isUploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                <T>Nahrať obrázok</T>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (file) void uploadImage(file)
                    event.target.value = ''
                  }}
                />
              </label>
            </div>
            {/* §9.435 - here, not 200 lines below. `data-upload-error` so the e2e can assert the
              * failure is reachable from the control that caused it. */}
            {uploadError !== '' && (
              <p className="text-xs text-destructive" data-upload-error>
                {uploadError}
              </p>
            )}
          </div>

          {/* §9.215 - *nastavenia viditeľnosti*, which the owner went looking for and did not find.
            * Three switches in three places were one decision: whether the product is on the menu,
            * whether the customer site draws it, and whether it can be ordered. */}
          <div className="border border-border rounded-xl p-4 space-y-3" data-visibility-settings>
            <h3 className="text-sm font-bold">
              <T>Nastavenia viditeľnosti</T>
            </h3>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(event) => setActive(event.target.checked)}
                  className="w-4 h-4 rounded accent-primary"
                />
                <span className="text-sm">
                  <T>Aktívny</T>
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={visibleOnWeb}
                  onChange={(event) => setVisibleOnWeb(event.target.checked)}
                  className="w-4 h-4 rounded accent-primary"
                />
                <span className="text-sm">
                  <T>Viditeľný na webe</T>
                </span>
              </label>
            </div>

            {/* §9.155, the owner's point 43 - the third state. *Neaktívny* takes the product off the
              * menu and *Viditeľný na webe* hides it from the customer site; this one leaves it
              * where it is and refuses the order, which is the whole point: the customer still sees
              * that the chips exist. */}
            <label
              className="flex items-start gap-2 cursor-pointer p-3 border border-border rounded-xl bg-muted/30"
              data-orderable-toggle
            >
              <input
                type="checkbox"
                checked={orderable}
                onChange={(event) => setOrderable(event.target.checked)}
                className="w-4 h-4 rounded accent-primary mt-0.5"
              />
              <span className="text-sm">
                <span className="font-semibold">
                  <T>Dostupný na objednanie</T>
                </span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  <T>
                    Po vypnutí zostane na webe viditeľný, ale zosivený a nedá sa objednať - napr. keď
                    dočasne došla surovina
                  </T>
                </span>
              </span>
            </label>
          </div>

          {/* §9.221 - the recipe, edited here rather than behind a button that opens a second modal.
              §9.181 answered *"the recipe is not reachable from the form"* with a control; legacy
              edits it **in** the form, and the owner asked for that four times. Collapsed to
              legacy's dashed button, expanding to the block `RecipeEditor` draws - the same one the
              standalone modal draws, so the two cannot drift. */}
          <div className="pt-4 mt-4 border-t border-border" data-recipe-section>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                <ChefHat className="w-3.5 h-3.5" /> <T>Recept</T>
              </span>
              {recipeLoading && (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
              )}
            </div>

            {/* §9.758 - nothing to show and nothing this person may add: the sentence instead of the button. */}
            {!recipeOpen && !recipeLoading && !mayEditRecipe && existingRecipe === undefined && (
              <p className="text-xs text-muted-foreground" data-recipe-read-only>
                {recipeRefusal()}
              </p>
            )}

            {!recipeOpen && !recipeLoading && (mayEditRecipe || existingRecipe !== undefined) && (
              <button
                type="button"
                data-open-recipe
                onClick={() => {
                  setRecipeOpen(true)
                  if (recipeRows.length === 0) setRecipeRows([{ ...EMPTY_INGREDIENT }])
                }}
                className="w-full py-2.5 rounded-xl border-2 border-dashed border-border text-sm font-semibold text-muted-foreground hover:border-primary/40 hover:text-foreground transition-all flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />{' '}
                {existingRecipe === undefined ? <T>Pridať recept</T> : <T>Zobraziť recept</T>}
              </button>
            )}

            {recipeOpen && !recipeLoading && (
              // §9.758 - one `disabled` for every control in the block, the ingredient rows included.
              <fieldset disabled={!mayEditRecipe} className="space-y-3 min-w-0 border-0 p-0 m-0" data-recipe-fields>
                {!mayEditRecipe && (
                  <p className="text-xs text-amber-500" data-recipe-read-only>
                    {recipeRefusal()}
                  </p>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={LABEL_CLASSES} htmlFor="product-kategoria-receptu">
                      <T>Kategória receptu</T>
                    </label>
                    <select
                      id="product-kategoria-receptu"
                      value={recipeCategory}
                      onChange={(event) => {
                        setRecipeTouched(true)
                        setRecipeCategory(event.target.value)
                      }}
                      aria-label={tt('Kategória receptu')}
                      className={FIELD_CLASSES}
                    >
                      {RECIPE_CATEGORIES.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={LABEL_CLASSES} htmlFor="product-popis-receptu">
                      <T>Popis receptu</T>
                    </label>
                    <input
                      id="product-popis-receptu"
                      value={recipeDescription}
                      onChange={(event) => {
                        setRecipeTouched(true)
                        setRecipeDescription(event.target.value)
                      }}
                      placeholder={tt('Krátky popis…')}
                      aria-label={tt('Popis receptu')}
                      className={FIELD_CLASSES}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={LABEL_CLASSES} htmlFor="product-priprava-min">
                      <T>Príprava (min)</T>
                    </label>
                    <input
                      id="product-priprava-min"
                      type="number"
                      min="0"
                      step="1"
                      value={recipePrepTime}
                      onChange={(event) => {
                        setRecipeTouched(true)
                        setRecipePrepTime(event.target.value)
                      }}
                      aria-label={tt('Príprava (min)')}
                      className={FIELD_CLASSES}
                    />
                  </div>
                  <div>
                    <label className={LABEL_CLASSES} htmlFor="product-porcie">
                      <T>Porcie</T>
                    </label>
                    <input
                      id="product-porcie"
                      type="number"
                      min="1"
                      step="1"
                      value={recipeServings}
                      onChange={(event) => {
                        setRecipeTouched(true)
                        setRecipeServings(event.target.value)
                      }}
                      aria-label={tt('Porcie')}
                      className={FIELD_CLASSES}
                    />
                  </div>
                </div>

                <RecipeEditor
                  ingredients={recipeRows}
                  onChange={(next) => {
                    setRecipeTouched(true)
                    setRecipeRows(next)
                  }}
                  // The whole argument for editing the recipe here: the margin needs the price, and
                  // the price is the field two inches above.
                  sellingPriceCents={parseMoneyToCents(price)}
                />
              </fieldset>
            )}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <div className="px-6 py-4 border-t border-border bg-muted/30 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl font-bold text-muted-foreground hover:bg-background transition-colors"
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            onClick={() => save()}
            /* FB-845, §9.559 - **a pizza whose rows are the hard-coded defaults, and nothing else.** `pizzaSizes` is
               null for anything that is not a pizza, so a drink can write nothing wrong; and a pizza that HAS stored
               sizes shows those either way, so refusing its save would block renaming it for no gain. What is left is
               exactly the live defect: a pizza with nothing stored, offered two sizes this franchise may not sell. */
            disabled={
              isSaving
              || (productType === 'pizza' && pizzaConfigFailed && (product?.pizzaSizes ?? null) === null)
            }
            className={cn('px-6 py-2.5 rounded-xl font-bold', BUTTON_PRIMARY)}
          >
            <T>Uložiť</T>
          </button>
        </div>
      </div>


    </div>
  )
}
